/*
  The KLUDI search overlay's behaviour.

  What is kept from focal and what is not:

  - kept: <openable-element> (assets/theme.js) as the base class. It owns the
    open attribute, the focus trap (escape-to-close and focus restoration to
    the header trigger come from it), the append-body move and the
    openable-element:open/close events the header's is="toggle-link" listens
    to for its aria-expanded handshake. None of that is re-implemented here
  - kept: focal's own way of getting results -- Shopify's predictive search
    endpoint rendered through a section, so the markup is authored in liquid.
    The debounce-then-abort shape of the request is the same idea, rewritten
    against this element rather than inherited from PredictiveSearchDrawer
  - replaced: PredictiveSearchDrawer itself. It is a DrawerContent subclass,
    and DrawerContent's whole job is the edge-anchored sliding panel plus its
    reverse-breakpoint direction flip. The design's panel does not slide and
    has no direction, so there was nothing left of it to use
*/

(() => {
  const OpenableElement = window.customElements.get('openable-element');
  if (!OpenableElement) return;

  // matches focal's own predictive search debounce (PredictiveSearchDrawer)
  const DEBOUNCE = 200;
  // the design's own result grid is three columns by two rows
  const RESULT_LIMIT = 6;
  const COMPACT_QUERY = '(max-width: 740px)';

  class KludiSearchOverlay extends OpenableElement {
    connectedCallback() {
      super.connectedCallback();

      this.inputElement = this.querySelector('.kludi-search__input');
      this.resultsElement = this.querySelector('.kludi-search__results');
      this.formElement = this.querySelector('.kludi-search__form');

      if (!this.inputElement || !this.resultsElement) return;

      // the server-rendered popular list, kept so clearing the field restores
      // the resting state without a second network round trip
      this.restingResultsHtml = this.resultsElement.innerHTML;

      this.inputElement.addEventListener('input', this._debounce(() => this._search(this.inputElement.value), DEBOUNCE));

      this.addEventListener('click', (event) => {
        const term = event.target.closest('.kludi-search__term');
        if (!term) return;

        this.inputElement.value = term.getAttribute('data-term') || term.textContent.trim();
        this.inputElement.focus();
        this._search(this.inputElement.value);
      });

      this._watchPlaceholder();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);

      // focal's DrawerContent does this for its drawers; the overlay is not a
      // drawer, so it locks the page itself rather than inheriting it
      if (name === 'open') {
        document.documentElement.classList.toggle('lock-all', this.open);

        // the design's own close resets the query with it, so reopening never
        // shows the previous visit's results under an empty field
        if (!this.open) this._reset();
      }
    }

    _reset() {
      // guards the attribute callback firing before connectedCallback has
      // captured the resting markup
      if (this.restingResultsHtml === undefined) return;
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.removeAttribute('loading');
      this.inputElement.value = '';
      this.resultsElement.innerHTML = this.restingResultsHtml;
    }

    /*
      the design writes a long placeholder on desktop and a short one on
      mobile. One input cannot carry two, so the value is swapped on the
      breakpoint -- with the wide one in the markup, which is what a
      no-javascript visitor and the /search page fallback see
    */
    _watchPlaceholder() {
      const media = window.matchMedia(COMPACT_QUERY);
      const apply = () => {
        const attribute = media.matches ? 'data-placeholder-compact' : 'data-placeholder-wide';
        const placeholder = this.inputElement.getAttribute(attribute);
        if (placeholder) this.inputElement.placeholder = placeholder;
      };

      media.addEventListener('change', apply);
      apply();
    }

    async _search(term) {
      const query = term.trim();

      if (this.abortController) this.abortController.abort();

      if (query === '') {
        this.abortController = null;
        this.removeAttribute('loading');
        this.resultsElement.innerHTML = this.restingResultsHtml;
        return;
      }

      const controller = new AbortController();
      this.abortController = controller;
      this.setAttribute('loading', '');

      try {
        const url = new URL(window.themeVariables.routes.predictiveSearchUrl, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', String(RESULT_LIMIT));
        url.searchParams.set('section_id', 'kludi-predictive-search');

        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`predictive search responded ${response.status}`);

        const wrapper = document.createElement('div');
        wrapper.innerHTML = await response.text();

        // the section endpoint answers with the section's own wrapper div
        this.resultsElement.innerHTML = wrapper.firstElementChild.innerHTML;
      } catch (error) {
        if (error.name === 'AbortError') return;
        // a failed lookup must not leave the last query's results standing as
        // if they answered this one
        this.resultsElement.innerHTML = this.restingResultsHtml;
      } finally {
        // only the request that is still the current one may clear the
        // loading state -- an aborted one is always followed by a newer
        // request that is still in flight
        if (this.abortController === controller) this.removeAttribute('loading');
      }
    }

    _debounce(fn, delay) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    }
  }

  window.customElements.define('kludi-search-overlay', KludiSearchOverlay);
})();
